import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GitMerge, Link2, Pencil, Plus, Trash2, Loader2, Save } from "lucide-react";
import { ScanTextInput } from "@/components/ScanTextInput";

const TYPE_BADGE = {
  "exact": "bg-emerald-600 text-white",
  "supersession": "bg-blue-600 text-white",
  "verified-interchange": "bg-teal-600 text-white",
  "conditional": "bg-amber-500 text-white",
  "ai-suggested-unverified": "bg-red-50 text-[#B01E28] border border-dashed border-[#B01E28]",
};
const TYPE_LABEL = {
  "exact": "EXACT", "supersession": "SUPERSESSION", "verified-interchange": "VERIFIED",
  "conditional": "CONDITIONAL", "ai-suggested-unverified": "UNVERIFIED",
};

export function InterchangeCard({ partId }) {
  const { has } = useAuth();
  const canManage = has("manage_interchange");
  const [data, setData] = useState(null);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ics, setIcs] = useState([]);
  const [chain, setChain] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/parts/${partId}/interchange`); setData(data); }
    catch (e) { /* part-view perms cover this */ }
  };
  useEffect(() => { load(); api.get("/inventory/constants").then(({ data }) => setTypes(data.interchange_types || [])).catch(() => {}); /* eslint-disable-next-line */ }, [partId]);

  const openEdit = () => {
    setIcs((data?.interchange_numbers || []).map((i) => ({ number: i.number || "", type: i.type || "verified-interchange", source: i.source || "", condition: i.condition || "" })));
    setChain((data?.lineage || []).join(" -> "));
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const supersession_chain = chain.split(/->|→|,/).map((s) => s.trim()).filter(Boolean);
    const interchange_numbers = ics.filter((i) => i.number.trim());
    try {
      await api.put(`/parts/${partId}/interchange`, { interchange_numbers, supersession_chain });
      toast.success("Interchange & supersession links saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const lineage = data.lineage || [];

  return (
    <div data-testid="part-interchange-card" className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 font-display font-bold text-slate-800"><GitMerge className="w-4 h-4 text-[#B01E28]" />Interchange & Supersession</div>
        {canManage && <Button data-testid="interchange-edit-btn" size="sm" variant="outline" className="rounded-lg border-slate-300 h-8" onClick={openEdit}><Pencil className="w-3.5 h-3.5 mr-1.5" />Manage links</Button>}
      </div>
      <div className="p-5 space-y-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Supersession lineage (oldest → current)</div>
          {lineage.length > 1 ? (
            <div data-testid="interchange-lineage" className="flex flex-wrap items-center gap-1 text-xs font-mono">
              {lineage.map((c, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className={`px-1.5 py-0.5 rounded ${i === lineage.length - 1 ? "bg-blue-50 text-blue-700 font-bold" : "bg-slate-100 text-slate-500"}`}>{c}</span>
                  {i < lineage.length - 1 && <span className="text-slate-400">→</span>}
                </span>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No supersession chain recorded.</p>}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Interchange / alternate numbers</div>
          {(data.interchange_numbers || []).length === 0 ? <p className="text-sm text-slate-400">No interchange numbers recorded.</p> : (
            <div className="space-y-1.5">
              {data.interchange_numbers.map((ic, i) => (
                <div key={i} data-testid={`interchange-row-${i}`} className="flex flex-wrap items-center gap-2 border border-slate-100 rounded-lg px-2.5 py-1.5 bg-slate-50">
                  <Link2 className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-mono text-xs font-bold text-slate-800">{ic.number}</span>
                  <Badge className={`rounded text-[8px] uppercase font-mono ${TYPE_BADGE[ic.type] || "bg-slate-200"}`}>{TYPE_LABEL[ic.type] || ic.type}</Badge>
                  {ic.source && <span className="font-mono text-[10px] text-slate-400">src: {ic.source}</span>}
                  {ic.condition && <span className="text-[10px] text-amber-700">· {ic.condition}</span>}
                  {ic.type === "ai-suggested-unverified" && <span className="text-[10px] text-[#B01E28]">— verify before relying</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-2xl" data-testid="interchange-dialog">
          <DialogHeader><DialogTitle className="font-display">Manage interchange & supersession</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65dvh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Supersession lineage</Label>
              <Input data-testid="interchange-chain-input" value={chain} onChange={(e) => setChain(e.target.value)} placeholder="oldest -> newer -> current OEM, e.g. 123456 -> 123455 -> 123454" className="rounded-lg border-slate-200 mt-1 font-mono text-sm" />
              <p className="text-[10px] text-slate-400 mt-1">Oldest on the left, the current OEM number on the right. Recorded centrally — applies to all current & future stock with this OEM.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Interchange / alternate numbers</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-lg border-slate-200 h-7" onClick={() => setIcs((s) => [...s, { number: "", type: "verified-interchange", source: "", condition: "" }])}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
              </div>
              <p className="text-[10px] text-[#B01E28] mb-2">Numbers are human-entered. Never auto-look-up OEM numbers; mark anything uncertain as "ai-suggested-unverified".</p>
              <div className="space-y-2">
                {ics.map((ic, i) => (
                  <div key={i} data-testid={`interchange-edit-row-${i}`} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-start border border-slate-100 rounded-lg p-2">
                    <ScanTextInput testid={`interchange-number-${i}`} placeholder="Number" value={ic.number}
                      onChange={(v) => setIcs((s) => s.map((x, j) => j === i ? { ...x, number: v } : x))}
                      inputClassName="text-sm" />
                    <Select value={ic.type} onValueChange={(v) => setIcs((s) => s.map((x, j) => j === i ? { ...x, type: v } : x))}>
                      <SelectTrigger data-testid={`interchange-type-${i}`} className="rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-lg">{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Source" value={ic.source} onChange={(e) => setIcs((s) => s.map((x, j) => j === i ? { ...x, source: e.target.value } : x))} className="rounded-lg border-slate-200 text-sm" />
                    <Button type="button" size="icon" variant="ghost" className="text-slate-400 hover:text-[#B01E28] h-9 w-9" onClick={() => setIcs((s) => s.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                    {ic.type === "conditional" && (
                      <Input data-testid={`interchange-condition-${i}`} placeholder="Condition (e.g. fits DD15 only)" value={ic.condition} onChange={(e) => setIcs((s) => s.map((x, j) => j === i ? { ...x, condition: e.target.value } : x))} className="rounded-lg border-amber-200 text-sm sm:col-span-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="interchange-save" disabled={saving} onClick={save} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save links</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
